
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setUserInfo, setCaptainData, setAuthLoading } from './store/userSlice';
import Splash from './pages/Splash';
import Login from './pages/Login';
import Guests from './pages/Guests';
import Invitation from './pages/Invitation'; // Added import
import Admin from './pages/Admin';
import FAQ from './pages/FAQ';
import PrivateRoute from './components/PrivateRoute';

import { getUserInfo, getCaptainData } from './api';

let isInitialLoad = true;

function App() {
  const dispatch = useDispatch();
  const isAuthLoading = useSelector((state) => state.user.isAuthLoading);
  const [authError, setAuthError] = React.useState(null);
  const [loadingProgress, setLoadingProgress] = React.useState(0);

  // Force splash page on initial load/hard-refresh (unless it's an invitation link)
  if (isInitialLoad) {
    isInitialLoad = false;
    const path = window.location.pathname.toLowerCase();
    if (!path.startsWith('/invitation') && path !== '/') {
      window.history.replaceState(null, '', '/');
    }
  }

  useEffect(() => {
    async function fetchUserInfo() {

      // Skip auth check for invitation page
      if (window.location.pathname.toLowerCase().startsWith('/invitation')) {
        dispatch(setAuthLoading(false));
        return;
      }

      // Ensure loading state is true at start
      dispatch(setAuthLoading(true));
      setLoadingProgress(10); // Start

      try {
        const clientPrincipal = await getUserInfo();
        setLoadingProgress(40); // User info fetched

        if (clientPrincipal) {
          dispatch(setUserInfo(clientPrincipal));
          setLoadingProgress(60); // Roles processed

          // Fetch captain data using the user's ID
          try {
            const captainData = await getCaptainData(clientPrincipal.userId);
            dispatch(setCaptainData(captainData));
          } catch (error) {
            console.error('Error fetching captain data:', error);
          }
          setLoadingProgress(90); // Captain data fetched
        } else {
          setLoadingProgress(90); // No user, getting ready to finish
        }
      } catch (err) {
        if (err.message === 'INCONSISTENT_ROLES') {
          setAuthError(true);
        } else {
          console.error('Unexpected auth error', err);
        }
      } finally {
        setLoadingProgress(100);
        setTimeout(() => {
          dispatch(setAuthLoading(false));
        }, 500); // Small delay to let bar fill
      }
    }
    fetchUserInfo();
  }, [dispatch]);

  return (
    <Router>
      <div className="app-wrapper">
        {isAuthLoading && !authError && (
          <div className="loading-overlay">
            <div className="loading-content">
              <h2>Loading...</h2>
              <p>Please wait while we fetch the latest details.</p>
              <div className="blur-text-container">
                <h1
                  className="blur-text"
                  style={{
                    filter: `blur(${Math.max(0, 20 - (loadingProgress / 100) * 20)}px)`,
                    opacity: Math.max(0.3, loadingProgress / 100)
                  }}
                >
                  Respect for Every Youth.
                </h1>
              </div>
            </div>
          </div>
        )}
        {authError && (
          <div className="error-overlay">
            <div className="error-modal">
              <h2>Authentication Error</h2>
              <p>
                Please sign in again! You were either recently approved for a table captain, or sign-in did not provide the right information.
              </p>
              <button onClick={() => window.location.href = '/.auth/logout'} className="logout-button">
                Sign Out
              </button>
            </div>
          </div>
        )}

        {/* Ambient Background Animation */}
        <div className="visual-ornament" />

        <Routes>
          <Route path="/" element={<Splash />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/guests"
            element={
              <PrivateRoute allowedRoles={['table_captain', 'admin', 'authenticated']}>
                <Guests />
              </PrivateRoute>
            }
          />
          <Route path="/invitation" element={<Invitation />} />
          <Route path="/faq" element={<FAQ />} />
          <Route
            path="/admin"
            element={(
              <PrivateRoute allowedRoles={['admin']}>
                <Admin />
              </PrivateRoute>
            )}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router >
  );
}

export default App;
